import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  expensePoliciesAPI,
  ExpensePolicy,
  ExpensePolicyCreate,
  ExpensePolicyUpdate,
} from '../../services/api';
import { useExpenseCategoryStore } from '../../store/expenseCategoryStore';

interface ExpensePolicyManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const inputClass =
  'w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent';

const emptyForm: ExpensePolicyCreate = {
  expense_category_id: undefined,
  limit_per_transaction: undefined,
  limit_daily: undefined,
  limit_monthly: undefined,
};

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '--';
  return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const ExpensePolicyManager: React.FC<ExpensePolicyManagerProps> = ({
  isOpen,
  onClose,
}) => {
  const { categories, fetchCategories } = useExpenseCategoryStore();

  const [policies, setPolicies] = useState<ExpensePolicy[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
  const [formData, setFormData] = useState<ExpensePolicyCreate>(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const loadPolicies = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await expensePoliciesAPI.list();
      setPolicies(data);
    } catch {
      setError('Failed to load expense policies.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadPolicies();
      fetchCategories();
    }
  }, [isOpen, loadPolicies, fetchCategories]);

  const resetForm = () => {
    setFormData(emptyForm);
    setShowAddForm(false);
    setEditingPolicyId(null);
  };

  const handleNumberChange = (
    field: 'limit_per_transaction' | 'limit_daily' | 'limit_monthly',
    value: string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value === '' ? undefined : parseFloat(value),
    }));
  };

  const handleCreate = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const created = await expensePoliciesAPI.create(formData);
      setPolicies((prev) => [...prev, created]);
      resetForm();
    } catch {
      setError('Failed to create policy.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingPolicyId) return;
    setIsSaving(true);
    setError(null);
    try {
      const updateData: ExpensePolicyUpdate = {
        expense_category_id: formData.expense_category_id,
        limit_per_transaction: formData.limit_per_transaction,
        limit_daily: formData.limit_daily,
        limit_monthly: formData.limit_monthly,
      };
      const updated = await expensePoliciesAPI.update(editingPolicyId, updateData);
      setPolicies((prev) => prev.map((p) => (p.id === editingPolicyId ? updated : p)));
      resetForm();
    } catch {
      setError('Failed to update policy.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    setError(null);
    try {
      await expensePoliciesAPI.delete(id);
      setPolicies((prev) => prev.filter((p) => p.id !== id));
      if (editingPolicyId === id) resetForm();
    } catch {
      setError('Failed to delete policy.');
    }
  };

  const startEdit = (policy: ExpensePolicy) => {
    setEditingPolicyId(policy.id);
    setShowAddForm(false);
    setFormData({
      expense_category_id: policy.expense_category_id ?? undefined,
      limit_per_transaction: policy.limit_per_transaction ?? undefined,
      limit_daily: policy.limit_daily ?? undefined,
      limit_monthly: policy.limit_monthly ?? undefined,
    });
  };

  const startAdd = () => {
    setEditingPolicyId(null);
    setFormData(emptyForm);
    setShowAddForm(true);
  };

  const renderForm = (isEdit: boolean) => (
    <div className="space-y-4 p-4 bg-gray-50 dark:bg-gray-700/40 rounded-lg border border-gray-200 dark:border-gray-600">
      <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
        {isEdit ? 'Edit Policy' : 'Add New Policy'}
      </h4>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Category
        </label>
        <select
          className={inputClass}
          value={formData.expense_category_id ?? ''}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              expense_category_id: e.target.value || undefined,
            }))
          }
        >
          <option value="">All categories (global policy)</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.icon ? `${cat.icon} ` : ''}
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Per Transaction Limit
          </label>
          <input
            type="number"
            className={inputClass}
            placeholder="No limit"
            min="0"
            step="0.01"
            value={formData.limit_per_transaction ?? ''}
            onChange={(e) => handleNumberChange('limit_per_transaction', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Daily Limit
          </label>
          <input
            type="number"
            className={inputClass}
            placeholder="No limit"
            min="0"
            step="0.01"
            value={formData.limit_daily ?? ''}
            onChange={(e) => handleNumberChange('limit_daily', e.target.value)}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Monthly Limit
          </label>
          <input
            type="number"
            className={inputClass}
            placeholder="No limit"
            min="0"
            step="0.01"
            value={formData.limit_monthly ?? ''}
            onChange={(e) => handleNumberChange('limit_monthly', e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button
          variant="primary"
          size="sm"
          isLoading={isSaving}
          onClick={isEdit ? handleUpdate : handleCreate}
        >
          {isEdit ? 'Save Changes' : 'Add Policy'}
        </Button>
        <Button variant="secondary" size="sm" onClick={resetForm}>
          Cancel
        </Button>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Expense Policies" size="lg">
      <div className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
            {error}
          </div>
        )}

        {/* Add Policy Button */}
        {!showAddForm && !editingPolicyId && (
          <Button variant="primary" size="sm" onClick={startAdd}>
            <PlusIcon className="h-4 w-4 mr-1.5" />
            Add Policy
          </Button>
        )}

        {/* Add Form */}
        {showAddForm && renderForm(false)}

        {/* Policies List */}
        {isLoading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Loading policies...
          </div>
        ) : policies.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            No expense policies configured yet.
          </div>
        ) : (
          <div className="space-y-3">
            {policies.map((policy) => (
              <div key={policy.id}>
                {editingPolicyId === policy.id ? (
                  renderForm(true)
                ) : (
                  <div
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      policy.is_active
                        ? 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 opacity-60'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {policy.category_icon && (
                          <span className="text-lg">{policy.category_icon}</span>
                        )}
                        <span className="font-medium text-gray-900 dark:text-white">
                          {policy.category_name ?? 'All Categories'}
                        </span>
                        {!policy.is_active && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500 dark:text-gray-400">
                        <span>
                          Per txn: {formatCurrency(policy.limit_per_transaction)}
                        </span>
                        <span>Daily: {formatCurrency(policy.limit_daily)}</span>
                        <span>Monthly: {formatCurrency(policy.limit_monthly)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4 shrink-0">
                      <button
                        onClick={() => startEdit(policy)}
                        className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                        title="Edit policy"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(policy.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Delete policy"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
